import React, { FC, ReactNode, useCallback, useEffect, useState, MouseEvent } from 'react';
import classNames from 'classnames';

import getClassName from 'src/common/utils/getClassName';
import Icon from 'src/common/components/IconDS22';
import { Theme, Variant, themes, variants } from './types';
import styles from './CostBreakdown.module.scss';

type DetailsToggleEvent = MouseEvent<HTMLDetailsElement>;

export type CostDetailsProps = {
  /** Formatted amount e.g. `$955.00` */
  amount: string;
  children: ReactNode;
  disableCollapse?: boolean;
  open?: boolean;
  theme?: Theme;
  title: string;
  variant?: Variant;
};

const CostDetails: FC<CostDetailsProps> = ({
  amount,
  children,
  disableCollapse = false,
  open: initiallyOpen = true,
  theme = themes[0],
  title,
  variant = variants[0],
}) => {
  const [open, setOpen] = useState(initiallyOpen);
  const toggleHandler = useCallback((event: DetailsToggleEvent) => {
    setOpen(event.currentTarget.open);
  }, []);

  useEffect(() => {
    setOpen(initiallyOpen);
  }, [initiallyOpen]);

  return (
    <details
      className={getClassName(styles, 'root', classNames(theme, variant, { disableCollapse }))}
      open={open}
      onToggle={toggleHandler}
    >
      <summary
        className={getClassName(styles, 'summary', classNames(variant, { disableCollapse }))}
        tabIndex={disableCollapse ? -1 : 0}
      >
        <span className={getClassName(styles, 'priceLabel', variant)}>
          {title}
        </span>
        <span
          className={styles.priceValue}
          data-test={`${title}-summary-price`}
        >
          {amount}
          <Icon
            id={open ? 'caret-up' : 'caret-down'}
            size="m"
          />
        </span>
      </summary>
      <div className={getClassName(styles, 'content', variant)}>
        {children}
      </div>
    </details>
  );
};

export default CostDetails;
